import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Building2, Wrench, Camera, UtensilsCrossed, GraduationCap,
  Megaphone, MapPin, PenLine, Briefcase, Sparkles, ArrowLeft, ArrowRight, Rocket, Copy, CheckCircle2, Eye, BookOpen, Landmark,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TYPES = [
  { id: "ecommerce", label: "E-Commerce Store", icon: ShoppingBag },
  { id: "business", label: "Business Website", icon: Building2 },
  { id: "service", label: "Service Business", icon: Wrench },
  { id: "portfolio", label: "Portfolio", icon: Camera },
  { id: "restaurant", label: "Restaurant", icon: UtensilsCrossed },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "agency", label: "Agency", icon: Megaphone },
  { id: "local", label: "Local Business", icon: MapPin },
  { id: "blog", label: "Blog", icon: PenLine },
  { id: "professional", label: "Professional", icon: Briefcase },
  { id: "course", label: "Online Courses", icon: BookOpen },
  { id: "real_estate", label: "Real Estate", icon: Landmark },
  { id: "other", label: "Other", icon: Sparkles },
];

const STEPS = ["Website Type", "Theme", "Business Info", "Settings & Access", "Launch"];

export default function WebsiteWizard() {
  const [step, setStep] = useState(0);
  const [themes, setThemes] = useState([]);
  const [form, setForm] = useState({
    type: "", theme_id: "", name: "", description: "", phone: "", email: "", address: "", category: "",
    instagram: "", facebook: "", currency: "INR", country: "India", timezone: "Asia/Kolkata", mode: "light",
    owner_name: "", owner_email: "", owner_password: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { api.get("/partner/themes").then((r) => setThemes(r.data)); }, []);

  // Returning from a live theme preview with a chosen theme
  useEffect(() => {
    const tid = location.state?.theme_id;
    if (tid && themes.length) {
      const t = themes.find((x) => x.id === tid);
      setForm((f) => ({ ...f, theme_id: tid, type: f.type || t?.website_types?.[0] || "ecommerce" }));
      setStep(1);
      toast.success(`Theme "${t?.name || ""}" selected`);
    }
  }, [location.state, themes]); // eslint-disable-line

  const themeOptions = useMemo(() => {
    const matching = themes.filter((t) => (t.website_types || []).includes(form.type));
    return matching.length ? matching : themes;
  }, [themes, form.type]);
  const selectedTheme = themes.find((t) => t.id === form.theme_id);

  const canNext = step === 0 ? !!form.type : step === 1 ? !!form.theme_id : step === 2 ? !!form.name : step === 3 ? !!form.owner_email : true;

  const PROGRESS = ["Preparing your website…", "Applying theme…", "Setting up your store…", "Configuring your pages…", "Almost ready…"];
  const [progressIdx, setProgressIdx] = useState(0);

  const create = async () => {
    setBusy(true);
    setProgressIdx(0);
    const timer = setInterval(() => setProgressIdx((i) => Math.min(i + 1, PROGRESS.length - 1)), 550);
    try {
      const [{ data }] = await Promise.all([
        api.post("/partner/websites", {
          name: form.name, type: form.type, theme_id: form.theme_id,
          business_info: { description: form.description, phone: form.phone, email: form.email, address: form.address, category: form.category, socials: { instagram: form.instagram, facebook: form.facebook } },
          settings: { currency: form.currency, country: form.country, timezone: form.timezone, mode: form.mode },
          owner_name: form.owner_name, owner_email: form.owner_email, owner_password: form.owner_password,
        }),
        new Promise((r) => setTimeout(r, 2600)),
      ]);
      clearInterval(timer);
      setResult(data);
      toast.success("Website created successfully");
    } catch (e) { clearInterval(timer); toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Email: ${result.owner.email}\nPassword: ${result.owner.password}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-10" data-testid="website-wizard">
      {busy && step === 4 && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-xl flex items-center justify-center" data-testid="creation-progress">
          <div className="text-center px-6">
            <div className="h-16 w-16 mx-auto rounded-2xl gradient-bg glow flex items-center justify-center animate-float">
              <Rocket className="h-7 w-7 text-white" />
            </div>
            <AnimatePresence mode="wait">
              <motion.p key={progressIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="font-display font-extrabold text-2xl tracking-tight mt-8" data-testid="progress-message">
                {PROGRESS[progressIdx]}
              </motion.p>
            </AnimatePresence>
            <div className="w-64 h-1.5 rounded-full bg-muted mx-auto mt-6 overflow-hidden">
              <motion.div className="h-full gradient-bg rounded-full" animate={{ width: `${((progressIdx + 1) / PROGRESS.length) * 100}%` }} transition={{ duration: 0.4 }} />
            </div>
          </div>
        </div>
      )}
      <div>
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${i < step ? "bg-primary text-primary-foreground border-transparent" : i === step ? "border-foreground" : "border-border text-muted-foreground"}`}>
                {i + 1}
              </div>
              <span className={`text-xs font-bold hidden sm:block ${i === step ? "" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-border" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}>
            {step === 0 && (
              <div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight mb-6">What are you building?</h2>
                <div className="grid sm:grid-cols-2 sm:grid-cols-3 gap-3">
                  {TYPES.map(({ id, label, icon: Icon }) => (
                    <button key={id} data-testid={`type-${id}`} onClick={() => setForm({ ...form, type: id })}
                      className={`border rounded-md p-5 text-left transition-all hover:border-foreground/50 ${form.type === id ? "border-foreground bg-muted" : "border-border"}`}>
                      <Icon className="h-5 w-5 mb-3" />
                      <p className="text-sm font-bold">{label}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight mb-2">Pick a theme</h2>
                <p className="text-sm text-muted-foreground mb-6">Presentation only — you can switch anytime without losing data.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[520px] overflow-y-auto pr-1">
                  {themeOptions.map((t) => (
                    <motion.div key={t.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} role="button" tabIndex={0}
                      data-testid={`theme-option-${t.id}`} onClick={() => setForm({ ...form, theme_id: t.id })}
                      className={`text-left border rounded-md overflow-hidden transition-all cursor-pointer ${form.theme_id === t.id ? "border-foreground ring-2 ring-foreground/20" : "border-border hover:border-foreground/40"}`}>
                      <div className="aspect-[16/9] bg-muted overflow-hidden relative group/img">
                        {t.preview && <img src={t.preview} alt={t.name} loading="lazy" className="w-full h-full object-cover" />}
                        <button data-testid={`preview-overlay-${t.id}`}
                          onClick={(e) => { e.stopPropagation(); window.open(`/preview/${t.id}`, "_blank", "noopener"); }}
                          className="absolute inset-0 bg-black/45 sm:opacity-0 sm:group-hover/img:opacity-100 opacity-100 sm:bg-black/45 bg-black/0 transition-opacity flex items-center justify-center">
                          <span className="px-5 py-2.5 rounded-full bg-white text-zinc-900 text-xs font-extrabold flex items-center gap-2 shadow-lg sm:scale-90 sm:group-hover/img:scale-100 transition-transform">
                            <Eye className="h-4 w-4" /> Live Preview
                          </span>
                        </button>
                        {form.theme_id === t.id && (
                          <span className="absolute top-3 left-3 px-3 py-1 rounded-full gradient-bg text-white text-[10px] font-extrabold uppercase tracking-widest">Selected</span>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <div><p className="font-bold text-sm">{t.name}</p><p className="text-xs text-muted-foreground">{t.category}</p>
                            {t.demo_products?.length > 0 && <p className="text-[10px] font-bold text-emerald-600 mt-0.5">Demo store content included</p>}</div>
                          <div className="flex gap-1">{Object.values(t.colors || {}).slice(0, 3).map((c, i) => <span key={i} className="h-3.5 w-3.5 rounded-full border border-border" style={{ backgroundColor: c }} />)}</div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button data-testid={`preview-theme-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); window.open(`/preview/${t.id}`, "_blank", "noopener"); }}
                            className="flex-1 py-2 rounded-full text-xs font-extrabold border border-border hover:bg-muted transition-colors flex items-center justify-center gap-1.5">
                            <Eye className="h-3.5 w-3.5" /> Preview Theme
                          </button>
                          <button data-testid={`use-theme-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); setForm({ ...form, theme_id: t.id }); setStep(2); }}
                            className="flex-1 py-2 rounded-full text-xs font-extrabold text-white gradient-bg glow-sm active:scale-95 transition-transform flex items-center justify-center gap-1.5">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Use This Theme
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight mb-6">Business information</h2>
                <div className="space-y-4 max-w-lg">
                  <div className="space-y-1.5"><Label className="overline-label">Business name *</Label>
                    <Input data-testid="biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="StyleHub Fashion" /></div>
                  <div className="space-y-1.5"><Label className="overline-label">Description</Label>
                    <Textarea data-testid="biz-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this business do?" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="overline-label">Phone</Label>
                      <Input data-testid="biz-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="overline-label">Email</Label>
                      <Input data-testid="biz-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="overline-label">Address</Label>
                    <Input data-testid="biz-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="overline-label">Category</Label>
                      <Input data-testid="biz-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Fashion" /></div>
                    <div className="space-y-1.5"><Label className="overline-label">Instagram</Label>
                      <Input data-testid="biz-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@handle" /></div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight mb-6">Settings & store access</h2>
                <div className="space-y-4 max-w-lg">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label className="overline-label">Currency</Label>
                      <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                        <SelectTrigger data-testid="set-currency"><SelectValue /></SelectTrigger>
                        <SelectContent>{["INR", "USD", "EUR", "GBP", "AED"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label className="overline-label">Country</Label>
                      <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                        <SelectTrigger data-testid="set-country"><SelectValue /></SelectTrigger>
                        <SelectContent>{["India", "United States", "United Kingdom", "UAE", "Singapore"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label className="overline-label">Mode</Label>
                      <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                        <SelectTrigger data-testid="set-mode"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="light">Light</SelectItem><SelectItem value="dark">Dark</SelectItem></SelectContent>
                      </Select></div>
                  </div>
                  <div className="border border-border rounded-md p-5 space-y-4">
                    <p className="overline-label">Store Admin Account (your client's login)</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="overline-label">Owner name</Label>
                        <Input data-testid="owner-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label className="overline-label">Owner email *</Label>
                        <Input data-testid="owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="owner@business.com" /></div>
                    </div>
                    <div className="space-y-1.5"><Label className="overline-label">Password (blank = auto-generate)</Label>
                      <Input data-testid="owner-password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} /></div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && !result && (
              <div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight mb-6">Review & launch</h2>
                <div className="border border-border rounded-md divide-y divide-border max-w-lg" data-testid="wizard-review">
                  {[["Type", TYPES.find((t) => t.id === form.type)?.label], ["Theme", selectedTheme?.name],
                    ["Business", form.name], ["Owner login", form.owner_email], ["Currency", form.currency]].map(([k, v]) => (
                    <div key={k} className="flex justify-between px-5 py-3.5 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-bold">{v}</span></div>
                  ))}
                </div>
                <Button data-testid="wizard-launch-button" onClick={create} disabled={busy}
                  className="mt-6 h-12 px-8 rounded-full font-bold text-base active:scale-95 transition-transform">
                  <Rocket className="h-4 w-4 mr-2" /> {busy ? "Creating…" : "Create Website"}
                </Button>
              </div>
            )}

            {step === 4 && result && (
              <div data-testid="wizard-success" className="max-w-lg">
                <CheckCircle2 className="h-14 w-14 text-emerald-500 mb-4" />
                <h2 className="font-display font-extrabold text-3xl tracking-tight">Website is live!</h2>
                <p className="text-muted-foreground mt-2">Share these store admin credentials with your client:</p>
                <div className="border border-border rounded-md p-5 mt-5 font-mono text-sm space-y-1.5 bg-card">
                  <p>Email: <span className="font-bold" data-testid="success-owner-email">{result.owner.email}</span></p>
                  <p>Password: <span className="font-bold" data-testid="success-owner-password">{result.owner.password}</span></p>
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Button data-testid="copy-credentials" variant="outline" onClick={copyCreds} className="rounded-full font-bold">
                    {copied ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />} {copied ? "Copied" : "Copy credentials"}
                  </Button>
                  <Button data-testid="view-new-store" onClick={() => window.open(`/site/${result.website.slug}`, "_blank")} className="rounded-full font-bold">View Store</Button>
                  <Button data-testid="back-to-websites" variant="ghost" onClick={() => navigate("/partner/websites")} className="rounded-full font-bold">My Websites</Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {!result && (
          <div className="flex gap-3 mt-10">
            {step > 0 && (
              <Button data-testid="wizard-back-button" variant="outline" onClick={() => setStep(step - 1)} className="rounded-full font-bold">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            )}
            {step < 4 && (
              <Button data-testid="wizard-next-button" disabled={!canNext} onClick={() => setStep(step + 1)} className="rounded-full font-bold active:scale-95 transition-transform">
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Right preview panel */}
      <div className="hidden lg:block">
        <div className="sticky top-24 rounded-md overflow-hidden border border-border">
          <div className="aspect-[4/3] bg-muted relative">
            {selectedTheme?.preview ? (
              <img src={selectedTheme.preview} alt="" className="w-full h-full object-cover" />
            ) : (
              <img src="https://images.pexels.com/photos/11382581/pexels-photo-11382581.jpeg" alt="" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="overline-label text-white/60">Live preview</p>
              <p data-testid="preview-name" className="font-display font-extrabold text-2xl tracking-tight mt-1">{form.name || "Your client's website"}</p>
              <p className="text-xs text-white/70 mt-1">{selectedTheme ? `${selectedTheme.name} theme` : "Select a theme in step 2"}</p>
            </div>
          </div>
          {selectedTheme && (
            <div className="p-5 bg-card flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Palette</span>
              <div className="flex gap-1.5">{Object.values(selectedTheme.colors || {}).slice(0, 5).map((c, i) => <span key={i} className="h-5 w-5 rounded-full border border-border" style={{ backgroundColor: c }} />)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
